import { Box, Menu, MenuItem, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Checkbox, IconButton } from '@sweep-io/sweep-design';
import { Layers } from '@sweep-io/sweep-design/dist/icons';
import React from 'react';
import { useDispatch } from 'react-redux';
import { VisibilityMap } from '../../../../../types/VisibilityTypes';
import { toggleLayersFor } from '../../../../../reducers/canvasLayersReducer';
import { VisibilityLayers } from '../../../../../types/enums/VisibilityLayers';
import get from 'lodash/get';

enum LayerOptionItemType {
  ENVIRONMENTS = 'ENVIRONMENTS',
  SALESFORCE_METADATA = 'SALESFORCE_METADATA',
  SWEEP_CONFIGURATION = 'SWEEP_CONFIGURATION',
  COMMENTS = 'COMMENTS',
}

const allLayerOptionTypes: LayerOptionItemType[] = [
  LayerOptionItemType.ENVIRONMENTS,
  LayerOptionItemType.SALESFORCE_METADATA,
  LayerOptionItemType.SWEEP_CONFIGURATION,
  LayerOptionItemType.COMMENTS,
];

const layersPerOptionTypes: Record<LayerOptionItemType, VisibilityLayers[]> = {
  [LayerOptionItemType.ENVIRONMENTS]: [
    VisibilityLayers.ENVIRONMENTS,
    VisibilityLayers.FUNNEL_STATUS,
  ],
  [LayerOptionItemType.SALESFORCE_METADATA]: [
    VisibilityLayers.APEX,
    VisibilityLayers.FUNNEL_SFDC_AUTOMATIONS,
    VisibilityLayers.FUNNEL_VALIDATION_RULES,
    VisibilityLayers.FUNNEL_APEX,
    VisibilityLayers.FUNNEL_ASSIGNMENT_RULES,
    VisibilityLayers.FUNNEL_DEDUPLICATION,
    VisibilityLayers.FUNNEL_MATCHING,
    VisibilityLayers.FUNNEL_SWEEP_AUTOMATIONS,
    VisibilityLayers.FUNNEL_PLAYBOOK_ALERTS,
    VisibilityLayers.FUNNEL_MATCHING_AND_DEDUPLICATION,
  ],
  [LayerOptionItemType.SWEEP_CONFIGURATION]: [
    VisibilityLayers.SWEEP_AUTOMATIONS,
    VisibilityLayers.NURTURING_STEPS,
    VisibilityLayers.GATES,
    VisibilityLayers.VALIDATION_RULES,
    VisibilityLayers.SFDC_AUTOMATIONS,
    VisibilityLayers.PLAYBOOK_ALERTS,
    VisibilityLayers.ASSIGNMENT_RULES,
    VisibilityLayers.SCHEDULED_ASSIGNMENTS,
  ],
  [LayerOptionItemType.COMMENTS]: [VisibilityLayers.COMMENTS],
};

const optionTypesTextMap: Record<LayerOptionItemType, string> = {
  [LayerOptionItemType.ENVIRONMENTS]: 'Environments',
  [LayerOptionItemType.SALESFORCE_METADATA]: 'Salesforce Metadata',
  [LayerOptionItemType.SWEEP_CONFIGURATION]: 'Sweep Configuration',
  [LayerOptionItemType.COMMENTS]: 'Comments',
};

interface LayerSelectorProps {
  visibilityMap: VisibilityMap;
  position?: 'top-left' | 'bottom-right';
  layersId: string;
  buttonVariant?: 'system' | 'flat';
}

const MenuItemContainer = styled(MenuItem)`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 16px;
`;

const LayerSelectorContainer = ({
  visibilityMap,
  position = 'top-left',
  layersId,
  buttonVariant = 'flat',
}: LayerSelectorProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const layers = Object.values(layersPerOptionTypes).map((layers) => get(layers, '[0]'));
  const layersCount = layers.filter((item) => visibilityMap[item]).length;

  return (
    <Box>
      <LayersSummary
        data-testid="layers-button"
        toggleOpen={handleClick}
        activeCount={layersCount}
        buttonVariant={buttonVariant}
      />
      <LayersPopover
        isOpen={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        visibilityMap={visibilityMap}
        position={position}
        layersId={layersId}
      />
    </Box>
  );
};

const LayersSummary = ({
  toggleOpen,
  activeCount,
  buttonVariant,
}: {
  toggleOpen: (event: React.MouseEvent<HTMLButtonElement>) => void;
  activeCount: number;
  buttonVariant?: 'system' | 'flat';
}) => {
  return (
    <Tooltip title="Layers" data-testid="layers-button">
      <IconButton onClick={toggleOpen} badgeLabel={activeCount} variant={buttonVariant}>
        <Layers variant="large" />
      </IconButton>
    </Tooltip>
  );
};

const menuPositionMap: any = {
  'top-left': {
    transformOrigin: { horizontal: 'center', vertical: -12 },
  },
  'bottom-right': {
    anchorOrigin: {
      vertical: -30,
      horizontal: -10,
    },
    transformOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
  },
};

const LayersPopover = ({
  isOpen,
  onClose,
  anchorEl,
  visibilityMap,
  position,
  layersId,
}: {
  isOpen: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  visibilityMap: VisibilityMap;
  environmentName?: string;
  position: 'top-left' | 'bottom-right';
  layersId: string;
}) => {
  const dispatch = useDispatch();

  const renderLayerMenuItem = (optionType: LayerOptionItemType) => {
    const layers = layersPerOptionTypes[optionType];
    const text = optionTypesTextMap[optionType];
    const isActive = layers.some((layer) => visibilityMap[layer]);
    return (
      <MenuItemContainer
        data-testid="layers-menu-button"
        key={optionType}
        onClick={() => dispatch(toggleLayersFor({ layersId, layers }))}
      >
        <VisibilityLayer isActive={isActive}>{text}</VisibilityLayer>
      </MenuItemContainer>
    );
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={isOpen}
      onClose={onClose}
      sx={{ '& .MuiMenu-paper': { width: '366px' } }}
      {...menuPositionMap[position]}
      data-testid="layers-menu"
      disablePortal
    >
      {allLayerOptionTypes.map(renderLayerMenuItem)}
    </Menu>
  );
};

const VisibilityLayer = ({
  children,
  isActive,
  icon: Icon,
}: {
  children: string;
  isActive: boolean;
  icon?: any;
}) => {
  return (
    <>
      {Icon && <Icon />}
      <Box sx={{ flex: 1 }}>{children}</Box>
      <Box>
        <Checkbox data-testid="layers-menu-checkbox" checked={isActive} />
      </Box>
    </>
  );
};

export default LayerSelectorContainer;
